import React, { useEffect, useState } from 'react';
import Divider from '@mui/material/Divider';
import './DLOnboardingView.less';
import NavigationBar from '../../../../components/NavigationBar/NavigationBar';
import { GA_TITLES, isDashboardScreen } from '../../../../../utils';
import BrandButton from '../../../../components/BrandButton/BrandButton';
import BrandIcon from '../../../../components/BrandIcon/BrandIcon';
import {
  isLocalizeAdded,
  LocalizePropType,
  UserDataOutput,
} from 'getaway-data-layer';
import ToggleTab from '../../../../components/ToggleTab/ToggleTab';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import CheckboxListItem from '../../../../components/CheckboxListItem/CheckboxListItem';
import { InputLabel, MenuItem, Select, TextField } from '@mui/material';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import { format, isBefore, isValid } from 'date-fns';
import { maxBirthDate, minBirthDate } from '../../../../../validation';
import RadioCardItem from '../../../../components/RadioCardItem/RadioCardItem';

type Props = {
  localize: LocalizePropType;
  user: UserDataOutput;
  onClose: () => void;
  onPressStart: () => void;
  scanEnabled: boolean;
  isPlasticCard: boolean;
  setIsPlasticCard: (arg: boolean) => void;
  onClickDisabled: () => void;
  supportedCountries: Array<any>;
  isLoading: boolean;
  showResubmissionUI?: boolean;
  resubmitInstruction?: string;
  isDLOld?: boolean;
  setIsDlOld?: (arg: boolean) => void;
  requireIDtoVerifyOldDL?: boolean;
  yearsToConsiderDLSelfieAsOld?: number;
  defaultCountry?: string;
};

const DLOnboardingView = ({
  user,
  localize,
  onClose,
  onPressStart,
  scanEnabled,
  isPlasticCard,
  setIsPlasticCard,
  onClickDisabled,
  supportedCountries,
  isLoading,
  showResubmissionUI,
  resubmitInstruction,
  isDLOld,
  setIsDlOld,
  requireIDtoVerifyOldDL,
  yearsToConsiderDLSelfieAsOld,
  defaultCountry,
}: Props) => {
  const dateFormat = 'dd/MM/yyyy';
  const steps = ['stepOne', 'stepTwo', 'stepThree', 'stepFour', 'stepFive'];
  const [country, setCountry] = useState(defaultCountry?.toUpperCase() || 'DE');
  const [dateOfBirth, setDateOfBirth] = useState(
    user?.dateOfBirth ? format(new Date(user?.dateOfBirth), 'yyyy-MM-dd') : ''
  );

  const [birthDateError, setBirthDateError] = useState<string | null>(null);

  const [paperSelectedItems, setPaperSelectedItems] = useState<boolean[]>(() =>
    steps.map(() => false)
  );

  const [paperHighlightedItems, setPaperHighlightedItems] = useState<boolean[]>(
    () => steps.map(() => false)
  );

  const [cardSelectedItems, setCardSelectedItems] = useState<boolean[]>(() =>
    steps.map(() => false)
  );

  const [cardHighlightedItems, setCardHighlightedItems] = useState<boolean[]>(
    () => steps.map(() => false)
  );

  const currentDate = new Date();
  const pastDate = new Date();
  pastDate.setFullYear(
    currentDate.getFullYear() - yearsToConsiderDLSelfieAsOld
  );

  const [DLRejectionReasonItem, setDLRejectionReasonItem] =
    useState<boolean>(false);

  const [
    DLRejectionReasonItemHighlighted,
    setDLRejectionReasonItemHighlighted,
  ] = useState<boolean>(false);

  const generatedStepsCount = steps.filter((step, index) => {
    const localizedText = localize(
      isPlasticCard ? `DL.${step}` : `DL.paper.${step}`
    );
    if (
      localizedText.includes('DL.step') ||
      localizedText.includes('DL.paper') ||
      localizedText.trim().length === 0
    ) {
      return false;
    }
    return true;
  })?.length;

  const checkCardItemsSelected =
    showResubmissionUI &&
    cardSelectedItems
      .slice(0, generatedStepsCount)
      .every((item) => item === true);

  const checkRejectionItemSelected =
    showResubmissionUI && DLRejectionReasonItem;

  const validateDOB = (dobStr: string) => {
    if (!dobStr) {
      setBirthDateError(null);
      return;
    }

    // Parse as date and check validity
    const parsedDOB = new Date(dobStr);
    if (!isValid(parsedDOB)) {
      setBirthDateError(localize('input.error.invalid'));
      return;
    }

    // Check if parsed date is above minBirthDate
    if (parsedDOB < minBirthDate) {
      setBirthDateError(localize('input.error.invalid'));
      return;
    }

    // Clear error if everything is okay
    setBirthDateError(null);
  };

  useEffect(() => {
    validateDOB(dateOfBirth);
  }, [dateOfBirth]);

  const dlAgeSelector = requireIDtoVerifyOldDL && (
    <>
      <div className="dl-age-title">{localize('dl.age.title')}</div>
      <div style={{ marginBottom: 24 }}>
        <RadioCardItem
          testid="organization"
          title={localize('dl.option.old', yearsToConsiderDLSelfieAsOld)}
          firstItem
          selected={isDLOld}
          onClick={() => setIsDlOld(true)}
        />
        <RadioCardItem
          testid="organization"
          title={localize('dl.option.not.old', format(pastDate, 'dd.MM.yyyy'))}
          lastItem
          selected={isDLOld !== undefined ? !isDLOld : undefined}
          onClick={() => setIsDlOld(false)}
        />
      </div>
      {isDLOld === undefined && (
        <div className="dl-age-footnote">{localize('dl.age.footnote')}</div>
      )}
    </>
  );

  const cardContent = [
    showResubmissionUI && resubmitInstruction && (
      <CheckboxListItem
        selected={DLRejectionReasonItem}
        title={localize('dl.rejected.title')}
        onPress={() => {
          setDLRejectionReasonItem((prev) => !prev);
          setDLRejectionReasonItemHighlighted(false);
        }}
        higlighted={DLRejectionReasonItemHighlighted}
        BottomComponent={
          <div
            data-testid="AccountTabView-readonlyDialog"
            className="account-DL-warning"
            onClick={() => {
              setDLRejectionReasonItem((prev) => !prev);
              setDLRejectionReasonItemHighlighted(false);
            }}
          >
            <div className="account-warning-msg">{resubmitInstruction}</div>
          </div>
        }
      />
    ),

    ...steps.map((step, index) => {
      const localizedText = localize(
        isPlasticCard ? `DL.${step}` : `DL.paper.${step}`
      );
      if (
        localizedText.includes('DL.step') ||
        localizedText.includes('DL.paper') ||
        localizedText.trim().length === 0
      ) {
        return null;
      }

      return (
        <React.Fragment key={index}>
          {showResubmissionUI ? (
            <CheckboxListItem
              key={index}
              selected={cardSelectedItems[index]}
              title={localizedText}
              onPress={() => {
                setCardSelectedItems((prev) => {
                  const newState = [...prev];
                  newState[index] = !prev[index];
                  return newState;
                });
                setCardHighlightedItems((prev) => {
                  const newState = [...prev];
                  newState[index] = false;
                  return newState;
                });
              }}
              higlighted={cardHighlightedItems[index]}
            />
          ) : (
            <>
              <div className="add-DL-item-container">
                <BrandIcon
                  name="selected"
                  style={{ width: 24, height: 24, marginRight: 18 }}
                />
                <div className="add-DL-item-text">{localizedText}</div>
                {index < steps.length - 1 && <Divider />}
              </div>
              <Divider />
            </>
          )}
        </React.Fragment>
      );
    }),
  ];

  const paperContent = (
    <div>
      <InputLabel id="country-label">
        {localize('DL.issuecountry.placeholder')}
      </InputLabel>
      <Select
        fullWidth
        required
        labelId="select-country"
        id="country"
        variant="standard"
        label={localize('DL.issuecountry.placeholder')}
        value={country}
        onChange={(event: any) => {
          setCountry(event.target.value);
        }}
        style={{ marginBottom: 16 }}
      >
        {supportedCountries.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.text}
          </MenuItem>
        ))}
      </Select>
      {showResubmissionUI && resubmitInstruction && country !== 'other' && (
        <CheckboxListItem
          selected={DLRejectionReasonItem}
          title={localize('dl.rejected.title')}
          onPress={() => {
            setDLRejectionReasonItem((prev) => !prev);
            setDLRejectionReasonItemHighlighted(false);
          }}
          higlighted={DLRejectionReasonItemHighlighted}
          BottomComponent={
            <div
              data-testid="AccountTabView-readonlyDialog"
              className="account-DL-warning"
              onClick={() => {
                setDLRejectionReasonItem((prev) => !prev);
                setDLRejectionReasonItemHighlighted(false);
              }}
            >
              <div className="account-warning-msg">{resubmitInstruction}</div>
            </div>
          }
        />
      )}
      {country !== 'other' &&
        steps.map((step, index) => {
          const localizedText = localize(
            isPlasticCard ? `DL.${step}` : `DL.paper.${step}`
          );
          if (
            localizedText.includes('DL.step') ||
            localizedText.includes('DL.paper') ||
            localizedText.trim().length === 0
          ) {
            return null;
          }

          if (country !== 'DE' && index === 0) {
            return null;
          }

          return (
            <CheckboxListItem
              key={index}
              selected={paperSelectedItems[index]}
              title={localizedText}
              onPress={() => {
                // For the first item in Germany, we have extra checks
                if (index === 0 && country === 'DE') {
                  const parsedDOB = new Date(dateOfBirth);
                  const isBefore1953 = isBefore(
                    parsedDOB,
                    new Date(1953, 0, 1)
                  );
                  // If invalid date, or userDOB >= 1953 => block toggle
                  if (!dateOfBirth || birthDateError || !isBefore1953) {
                    return;
                  }
                }
                // Toggle the state for this checkbox
                setPaperSelectedItems((prev) => {
                  const newState = [...prev];
                  newState[index] = !prev[index];
                  return newState;
                });
                setPaperHighlightedItems((prev) => {
                  const newState = [...prev];
                  newState[index] = false;
                  return newState;
                });
              }}
              higlighted={paperHighlightedItems[index]}
              BottomComponent={
                index === 0 && (
                  <div className="DL-date-wrapper">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                          autoFocus={false}
                          showToolbar={false}
                          inputFormat={dateFormat}
                          label={localize('schufa.dateOfBirth.label')}
                          minDate={minBirthDate}
                          maxDate={maxBirthDate}
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              variant="standard"
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                              }}
                              sx={{
                                width: '100%',
                              }}
                              error={false}
                            />
                          )}
                          disabled={user?.dateOfBirthVerified}
                          value={dateOfBirth}
                          onChange={(value: any) => {
                            if (value instanceof Date && isValid(value)) {
                              setDateOfBirth(format(value, 'yyyy-MM-dd'));
                              const parsedDOB = new Date(value);
                              const isBefore1953 = isBefore(
                                parsedDOB,
                                new Date(1953, 0, 1)
                              );
                              isBefore1953 &&
                                setPaperHighlightedItems((prev) => {
                                  const newState = [...prev];
                                  newState[index] = false;
                                  return newState;
                                });
                            } else {
                              // If user clears input or invalid => set empty
                              setDateOfBirth('');
                            }
                          }}
                        />
                      </LocalizationProvider>

                      {!dateOfBirth && (
                        <BrandIcon
                          name="iconIncomplete"
                          style={{
                            width: 10,
                            height: 10,
                            marginLeft: '10px',
                            marginTop: '15px',
                          }}
                        />
                      )}
                    </div>
                    {/* Show general parse/min-birth-date errors */}
                    {birthDateError && (
                      <div className="add-DL-warning">{birthDateError}</div>
                    )}
                    {/* Show special "notvalid" if DE user and date >= 1953 */}
                    {!birthDateError &&
                      dateOfBirth &&
                      isValid(new Date(dateOfBirth)) &&
                      !isBefore(
                        new Date(dateOfBirth),
                        new Date(1953, 0, 1)
                      ) && (
                        <div className="add-DL-warning">
                          {localize('DL.paper.date.notvalid')}
                        </div>
                      )}
                  </div>
                )
              }
            />
          );
        })}

      {country === 'other' && (
        <div className="add-DL-warning">
          {localize('DL.paper.country.notvalid')}
        </div>
      )}
    </div>
  );

  // Get the indices of the visible paper items (skip any null items)
  const visiblePaperIndices = steps.reduce<number[]>((acc, step, index) => {
    const localizedText = localize(
      isPlasticCard ? `DL.${step}` : `DL.paper.${step}`
    );
    if (
      !(
        localizedText.includes('DL.step') ||
        localizedText.includes('DL.paper') ||
        localizedText.trim().length === 0
      )
    ) {
      acc.push(index);
    }
    return acc;
  }, []);

  // All visible checkboxes selected
  const allPaperSelected = visiblePaperIndices.every((index) =>
    index === 0 && country !== 'DE' ? true : paperSelectedItems[index]
  );

  useEffect(() => {
    const parsedDOB = new Date(dateOfBirth);
    if (
      country === 'DE' &&
      isValid(parsedDOB) &&
      !birthDateError &&
      isBefore(parsedDOB, new Date(1953, 0, 1))
    ) {
      setPaperSelectedItems((prev) => {
        const newState = [...prev];
        newState[0] = true;
        return newState;
      });
    } else {
      setPaperSelectedItems((prev) => {
        const newState = [...prev];
        newState[0] = false;
        return newState;
      });
    }
  }, [country, dateOfBirth, birthDateError]);

  const handleClick = () => {
    if (isPlasticCard) {
      !showResubmissionUI && onPressStart();

      if (showResubmissionUI) {
        if (!checkRejectionItemSelected || !checkCardItemsSelected) {
          !checkRejectionItemSelected &&
            setDLRejectionReasonItemHighlighted(true);

          !checkCardItemsSelected &&
            cardSelectedItems.forEach((step, index) => {
              if (step === false) {
                setCardHighlightedItems((prev) => {
                  const newState = [...prev];
                  newState[index] = true;
                  return newState;
                });
              }
            });
        } else {
          onPressStart();
        }
      }
    }

    if (!isPlasticCard) {
      if (!allPaperSelected && !showResubmissionUI) {
        paperSelectedItems.forEach((step, index) => {
          if (!step) {
            setPaperHighlightedItems((prev) => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
          }
        });
      } else if (allPaperSelected && !showResubmissionUI) {
        onPressStart();
      }
      //
      else if (
        (!allPaperSelected || !checkRejectionItemSelected) &&
        showResubmissionUI
      ) {
        !checkRejectionItemSelected &&
          setDLRejectionReasonItemHighlighted(true);

        !allPaperSelected &&
          paperSelectedItems.forEach((step, index) => {
            if (!step) {
              setPaperHighlightedItems((prev) => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
            }
          });
      }
      //
      else if (
        allPaperSelected &&
        showResubmissionUI &&
        checkRejectionItemSelected
      ) {
        onPressStart();
      }
    }
  };

  return (
    <div className="screen-container">
      <NavigationBar
        title={localize('DL.title')}
        description={localize('DL.desr')}
        onClose={onClose}
        showHelp
        showArrowBack={isDashboardScreen()}
      />
      <div
        className={
          isLocalizeAdded(localize, 'DL.faq')
            ? 'screen-body-faq'
            : 'screen-body'
        }
      >
        <div
          className={
            isLocalizeAdded(localize, 'DL.faq')
              ? 'screen-body-content-faq'
              : 'screen-body-content'
          }
        >
          <ToggleTab
            localize={localize}
            optionOne={localize(`dl.tab.plastic`)}
            optionTwo={localize(`dl.tab.paper`)}
            isOptionOneSelected={isPlasticCard}
            onToggleValueChange={() => {
              setIsPlasticCard(!isPlasticCard);
              setPaperSelectedItems((prev) =>
                prev.map((item, index) => (index === 0 ? item : false))
              );
              setCardSelectedItems((prev) => prev.map(() => false));
              setDLRejectionReasonItem(false);
              setDLRejectionReasonItemHighlighted(false);
              setCardHighlightedItems((prev) => {
                return prev.map(() => false);
              });
              setPaperHighlightedItems((prev) => {
                return prev.map(() => false);
              });
            }}
          />

          <div className="add-DL-img-container">
            <img
              alt="DL"
              src={
                isPlasticCard ? '/images/DL-de.png' : '/images/DL-paper-de.png'
              }
              className="add-DL-img"
            />
          </div>

          {isPlasticCard && dlAgeSelector}

          {requireIDtoVerifyOldDL && isDLOld === undefined && isPlasticCard
            ? null
            : isPlasticCard
            ? cardContent
            : paperContent}

          <BrandButton
            id={GA_TITLES.ON_BOARDING.ADD_DL}
            title={localize('DL.button.text')}
            onClick={handleClick}
            disabled={
              (requireIDtoVerifyOldDL &&
                isDLOld === undefined &&
                isPlasticCard) ||
              (isPlasticCard
                ? isLoading || !scanEnabled
                : isLoading || !scanEnabled || country === 'other')
            }
            onClickDisabled={
              (requireIDtoVerifyOldDL &&
                isDLOld === undefined &&
                isPlasticCard) ||
              showResubmissionUI
                ? () => {}
                : isPlasticCard
                ? onClickDisabled
                : allPaperSelected &&
                  country !== 'other' &&
                  // check for checkRejectionItemSelected if its required, otherwise assume true
                  (showResubmissionUI ? checkRejectionItemSelected : true)
                ? onClickDisabled
                : () => {}
            }
            isLoading={isLoading}
            enableKeyboardEffect={false}
          />
        </div>

        {isLocalizeAdded(localize, 'DL.faq') ? (
          <div className="faq">
            <Markdown rehypePlugins={[rehypeRaw]}>
              {localize('DL.faq')}
            </Markdown>
          </div>
        ) : (
          <>
            <div className="screen-vertical-space" />
            <div className="screen-vertical-space" />
          </>
        )}

        <div className="screen-vertical-space" />
        <div className="screen-vertical-space" />
      </div>
    </div>
  );
};

export default DLOnboardingView;
